import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import AnimatedBadge from './AnimatedBadge';
import { ShieldCheck, ClipboardCheck, Download, Lock, Globe, Timer, DollarSign, FileText } from 'lucide-react';

const rawApi = process.env.REACT_APP_API_URL || '';
const API_URL = (rawApi || '').replace(/\/+$/, '');

const Landing = ({ onGetStarted }) => {
  const [saved, setSaved] = useState({ users: 0, savedUSD: 0, perUser: 300 });
  const [display, setDisplay] = useState(0);
  const videoRef = useRef(null);

  useEffect(() => {
    let mounted = true;
    axios.get(`${API_URL}/api/stats/saved`).then((res) => {
      if (!mounted) return;
      const data = res.data || {};
      setSaved({
        users: data.users || 0,
        savedUSD: data.savedUSD || 0,
        perUser: data.perUser || 300
      });
    }).catch(() => {});
    return () => { mounted = false; };
  }, []);

  // Simple count-up animation
  useEffect(() => {
    const target = saved.savedUSD || 0;
    let start = 0;
    const step = Math.max(1, Math.floor(target / 60));
    const timer = setInterval(() => {
      start += step;
      if (start >= target) {
        start = target;
        clearInterval(timer);
      }
      setDisplay(start);
    }, 16);
    return () => clearInterval(timer);
  }, [saved.savedUSD]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return undefined;

    const timer = setTimeout(() => {
      const playPromise = video.play();
      if (typeof playPromise?.catch === 'function') {
        playPromise.catch(() => {});
      }
    }, 1000);

    return () => {
      clearTimeout(timer);
    };
  }, []);

  return (
    <div className="container">
      <div className="page-badge"><AnimatedBadge size={66} /></div>
      <div className="card">
        <h1>Visa Document Assistant</h1>
        <p className="subtitle">
          Service that guides you step-by-step through collecting your visa documents. Save hundreds of dollars otherwise spent on agencies.
        </p>

        {/* Landing video */}
        <div className="landing-video">
          <video
            className="landing-video-player"
            ref={videoRef}
            src="/Super%20compressed%20video%20for%20lending%20page%20visa-docs.mp4"
            controls
            muted
            playsInline
            preload="metadata"
          />
        </div>

        {/* Savings indicator under the video */}
        <div className="landing-saved" style={{ marginBottom: 4 }}>
          <span className="saved-value">${display.toLocaleString()}</span>
          <span className="saved-text"> already saved on agencies for our customers</span>
        </div>

        <div className="feature-badges">
          <div className="badge-card">
            <div className="badge-icon"><ShieldCheck size={20} /></div>
            <div>
              <div className="badge-title">No agency fees</div>
              <div className="badge-text">Clear, step‑by‑step guidance without upsells.</div>
            </div>
          </div>
          <div className="badge-card">
            <div className="badge-icon"><ClipboardCheck size={20} /></div>
            <div>
              <div className="badge-title">Accurate checklist</div>
              <div className="badge-text">Tailored to your embassy and purpose.</div>
            </div>
          </div>
          <div className="badge-card">
            <div className="badge-icon"><FileText size={20} /></div>
            <div>
              <div className="badge-title">Smart document checks</div>
              <div className="badge-text">AI flags issues before your appointment.</div>
            </div>
          </div>
          <div className="badge-card">
            <div className="badge-icon"><Download size={20} /></div>
            <div>
              <div className="badge-title">One‑click downloads</div>
              <div className="badge-text">Keep files ready and share instantly.</div>
            </div>
          </div>
          <div className="badge-card">
            <div className="badge-icon"><Lock size={20} /></div>
            <div>
              <div className="badge-title">Secure storage</div>
              <div className="badge-text">Your data stays private and encrypted.</div>
            </div>
          </div>
          <div className="badge-card">
            <div className="badge-icon"><Globe size={20} /></div>
            <div>
              <div className="badge-title">Works anywhere</div>
              <div className="badge-text">Mobile‑friendly on any device.</div>
            </div>
          </div>
          <div className="badge-card">
            <div className="badge-icon"><Timer size={20} /></div>
            <div>
              <div className="badge-title">Save time</div>
              <div className="badge-text">Arrive prepared and avoid rework.</div>
            </div>
          </div>
          <div className="badge-card">
            <div className="badge-icon"><DollarSign size={20} /></div>
            <div>
              <div className="badge-title">Save money</div>
              <div className="badge-text">Avoid agency fees — do it yourself with confidence.</div>
            </div>
          </div>
        </div>

        <div className="landing-cta" style={{ marginTop: 24 }}>
          <button className="btn btn-primary" onClick={onGetStarted}>Get started — it’s free</button>
        </div>
      </div>
    </div>
  );
};

export default Landing;
